import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  type OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Httpservice } from '../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../helper/apiconstant';
import { environment } from '../../environments/environment';
import { encryptLoginPayload } from '../../helper/login-crypto';
import { CaptchaResponseValue, SignInPayload, SignInResponseValue } from './signin.interfaces';
import { Commonservice } from '../../services/commonservice';
import { ManageSession } from '../../services/manage-session';
import { SessionService } from '../../services/session.service';
import { ServerTimeService } from '../../services/server-time';

@Component({
  selector: 'app-signin',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './signin.html',
  styleUrl: './signin.scss',
})
export class Signin implements OnInit {
  @ViewChild('demoMenu') demoMenu?: ElementRef<HTMLDivElement>;

  protected readonly logoPath =
    'assets/images/authentication/nea-logo.png';

  constructor(
    private readonly router: Router, 
    private readonly httpSvc: Httpservice,
    private readonly commonSvc: Commonservice,
    private readonly sessionSvc: ManageSession,
    private readonly appSessionSvc: SessionService,
    private readonly serverTimeSvc: ServerTimeService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  protected username = '';
  protected password = '';
  protected error = '';
  protected showPassword = false;
  protected rememberMe = false;
  protected loginSuccess = false;
  protected captchaId = '';
  protected captchaImage = '';
  protected captchaResponse = '';
  protected captchaLoading = false;
  protected captchaBlockedUntil = 0;

  ngOnInit(): void {
    const remembered = this.sessionSvc.getRememberMeCredentials();
    if (remembered) {
      this.username = remembered.username;
      this.password = remembered.password;
      this.rememberMe = true;
    }

    if (this.commonSvc.isLoggedIn) {
      const isVerified = this.appSessionSvc.getVerificationState();
      this.router.navigate([isVerified === false ? '/update-password' : '/dashboard']);
      return;
    }
    this.loadCaptcha();
  }

  protected async onSignin(): Promise<void> {
    this.error = '';
    this.loginSuccess = false;
    if (this.isCaptchaBlocked()) {
      this.error = `Too many incorrect CAPTCHA attempts. Please try again in ${this.getCaptchaLockoutRemainingLabel()}.`;
      return;
    }

    if (!this.username || !this.password) {
      this.error = 'Please enter both username and password';
      return;
    }
    if (!this.captchaId || !this.captchaResponse.trim()) {
      this.error = 'Please enter the CAPTCHA';
      return;
    }
    const payload: SignInPayload = {
      username: this.username,
      password: this.password,
      captcha_id: this.captchaId,
      captcha_response: this.captchaResponse.trim(),
    };

    let encryptedPayload: SignInPayload;
    try {
      encryptedPayload = await encryptLoginPayload(payload, environment.loginPublicKey);
    } catch {
      this.error = 'Unable to secure login request. Please try again.';
      this.cdr.markForCheck();
      return;
    }

    this.httpSvc.requestCall(AuthEndPoints.LOGIN, ApiMethod.POST, encryptedPayload)
    .subscribe((data) => {
      const response = data?.value as SignInResponseValue | undefined;
      const authenticated = this.isSigninAuthenticated(data, response);

      if (authenticated && response?.token) {
        this.loginSuccess = true;
        this.serverTimeSvc.initialize(response?.current_time, response?.current_time_bs);
        this.commonSvc.setAuthContext(
          response.token,
          response?.menu_json ?? null,
          response?.is_verified ?? null,
          {
            user_id: response?.user_id ?? null,
            username: response?.username ?? null,
            user_fullname: response?.user_fullname ?? null,
            email: response?.email ?? null,
            mobile_no: response?.mobile_no ?? null,
            role_name: response?.role_name ?? null,
            role_display_name: response?.role_display_name ?? null,
            substation_id: response?.substation_id ?? null,
            generator_id: response?.generator_id ?? null,
            profile_image: response?.profile_image ?? null,
          },
        );
        if (this.rememberMe) {
          this.sessionSvc.saveRememberMeCredentials({
            username: this.username,
            password: this.password,
          });
        } else {
          this.sessionSvc.clearRememberMeCredentials();
        }
        this.router.navigate([response?.is_verified === false ? '/update-password' : '/dashboard']);
      } else {
        this.commonSvc.clearSession();
        this.error = this.resolveSigninErrorMessage(data);
        this.applyCaptchaLockoutState(data);
        if (!this.isCaptchaBlocked()) {
          this.refreshCaptcha();
        }
        this.cdr.markForCheck();
      }
    }, (error) => {
      this.commonSvc.clearSession();
      const response = error?.error ?? error;
      this.error = this.resolveSigninErrorMessage(response);
      this.applyCaptchaLockoutState(response);
      if (!this.isCaptchaBlocked()) {
        this.refreshCaptcha();
      }
      this.cdr.markForCheck();
    });
  }

  protected refreshCaptcha(): void {
    if (this.isCaptchaBlocked()) {
      this.error = `Too many incorrect CAPTCHA attempts. Please try again in ${this.getCaptchaLockoutRemainingLabel()}.`;
      return;
    }
    this.captchaResponse = '';
    this.loadCaptcha();
  }

  private loadCaptcha(): void {
    this.captchaLoading = true;
    this.httpSvc.requestCall(AuthEndPoints.CAPTCHA, ApiMethod.GET, '', false, { suppressToast: true })
      .subscribe({
        next: (data) => {
          const value = data?.value as CaptchaResponseValue | undefined;
          this.captchaId = value?.captcha_id ?? '';
          this.captchaImage = value?.captcha_image ?? '';
          this.captchaLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.captchaId = '';
          this.captchaImage = '';
          this.captchaLoading = false;
          this.error = 'Unable to load CAPTCHA. Please refresh and try again.';
          this.cdr.markForCheck();
        },
      });
  }

  private isSigninAuthenticated(response: any, value: any): boolean {
    const status = typeof response?.status === 'string' ? response.status.toUpperCase() : '';

    if (status === 'TRUE') {
      return Boolean(value?.authenticated);
    }

    if (status === 'FALSE') {
      return false;
    }

    return Boolean(value?.authenticated);
  }

  private resolveSigninErrorMessage(response: any): string {
    const message = typeof response?.msg === 'string' ? response.msg : '';
    const authenticated = response?.value?.authenticated;

    if (message) {
      return message;
    }

    if (authenticated === false) {
      return 'Invalid username or password';
    }

    return 'Invalid username or password';
  }

  private applyCaptchaLockoutState(response: any): void {
    const blockedSeconds = Number(response?.value?.blocked_seconds ?? 0);
    if (!Number.isFinite(blockedSeconds) || blockedSeconds <= 0) {
      this.captchaBlockedUntil = 0;
      return;
    }

    this.captchaBlockedUntil = Date.now() + blockedSeconds * 1000;
  }

  private isCaptchaBlocked(): boolean {
    if (!this.captchaBlockedUntil) {
      return false;
    }

    if (Date.now() >= this.captchaBlockedUntil) {
      this.captchaBlockedUntil = 0;
      return false;
    }

    return true;
  }

  private getCaptchaLockoutRemainingLabel(): string {
    const remainingSeconds = Math.max(Math.ceil((this.captchaBlockedUntil - Date.now()) / 1000), 0);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    if (minutes && seconds) {
      return `${minutes} minute(s) ${seconds} second(s)`;
    }
    if (minutes) {
      return `${minutes} minute(s)`;
    }
    return `${seconds} second(s)`;
  }

  protected togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  protected goToForgotPassword(): void {
    this.router.navigate(['/forgot-password']);
  }

  protected onChangeRememberMe(checked: boolean) {
    this.rememberMe = checked;
    if (!checked) {
      this.sessionSvc.clearRememberMeCredentials();
    }
  }
}
